/**
 * timeOfDay — single source of truth for the dashboard's time-aware greeting.
 * Slot detection, eyebrow copy, rotating micro-copy, and the matching CSS gradient
 * token name (defined in index.css) used by both WelcomeBanner and TodayHeroCard.
 */

export type TimeSlot = 'morning' | 'afternoon' | 'evening' | 'night';

export function getTimeSlot(date: Date = new Date()): TimeSlot {
  const h = date.getHours();
  if (h >= 5 && h < 12) return 'morning';
  if (h >= 12 && h < 17) return 'afternoon';
  if (h >= 17 && h < 21) return 'evening';
  return 'night';
}

const EYEBROW: Record<TimeSlot, string> = {
  morning: 'Good morning',
  afternoon: 'Afternoon check-in',
  evening: 'Evening wind-down',
  night: 'Late night',
};

const MICROCOPY: Record<TimeSlot, string[]> = {
  morning: [
    'Three small steps today beat one giant leap.',
    'Pick one thing to make today feel like a win.',
    'Mornings are momentum. Start anywhere.',
    'A two-minute task is still a task done.',
  ],
  afternoon: [
    'Halfway there — what still matters?',
    'A quick check-in keeps the day on rails.',
    'Knock one thing off. Future-you will thank you.',
    'Reset, refocus, reroute if needed.',
  ],
  evening: [
    'Wind down with one finishing move.',
    'Close a loop before the day closes.',
    'Reflect on a win — even a small one counts.',
    'Tomorrow starts with what you finish tonight.',
  ],
  night: [
    'Quiet hours are great for big-picture goals.',
    'Plant a seed for tomorrow.',
    'Rest is part of the plan, too.',
    'A small note now becomes a clear morning.',
  ],
};

function dayOfYear(d: Date) {
  const start = new Date(d.getFullYear(), 0, 0);
  const diff = d.getTime() - start.getTime();
  return Math.floor(diff / 86400000);
}

export function pickMicrocopy(slot: TimeSlot, date: Date = new Date()): string {
  const list = MICROCOPY[slot];
  return list[dayOfYear(date) % list.length];
}

export function getEyebrow(slot: TimeSlot): string {
  return EYEBROW[slot];
}

/** Resolve a friendly first-name salutation from profile/email. */
export function resolveFirstName(displayName?: string | null, email?: string | null): string {
  const raw =
    (displayName && displayName.trim()) ||
    (email ? email.split('@')[0] : '') ||
    '';
  // Strip any stray '@' (covers cases where display_name was set to a full
  // email) and split on common separators.
  const cleaned = raw.split('@')[0];
  const token = cleaned.split(/[\s._-]+/).filter(Boolean)[0] || '';
  if (!token) return 'friend';
  return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
}

/** Tailwind class used to scope the --tone-gradient CSS variable to a subtree. */
export function toneClass(slot: TimeSlot): string {
  return `tone-${slot}`;
}
